import { useMemo } from "react";
import { Typography } from "@/components/ui";
import { formatContactsName, formatUsersName } from "@/lib/utils/format";
import { SignatureRequestPdfLayoutEditor } from "../SignatureRequestPdfLayoutEditor";
import type {
  LayoutField,
  PendingSignerSlot,
  SignerSlot,
} from "../SignatureRequestPdfLayoutEditor.constants";
import type { SignatureRequestPreviewDocument } from "./SignatureRequestDocumentPreview";
import {
  getSignatureRequestSignerSlotIndex,
  isContactSigner,
  isUserSigner,
  type SignatureRequestSigner,
} from "../signatureRequestSigners";

interface PlaceFieldsStepProps {
  previewDocument: SignatureRequestPreviewDocument | null;
  signers: SignatureRequestSigner[];
  layoutFields: LayoutField[];
  onLayoutFieldsChange: (fields: LayoutField[]) => void;
  activeSignerId: string;
  onActiveSignerChange: (signerId: string) => void;
  onRequestSignerSlot: (slot: {
    index: number;
    kind: SignatureRequestSigner["kind"];
  }) => void;
  onPendingSignerSlotsChange: (
    slots: PendingSignerSlot[],
    templateSlots: PendingSignerSlot[],
  ) => void;
}

export function PlaceFieldsStep({
  previewDocument,
  signers,
  layoutFields,
  onLayoutFieldsChange,
  activeSignerId,
  onActiveSignerChange,
  onRequestSignerSlot,
  onPendingSignerSlotsChange,
}: PlaceFieldsStepProps) {
  const fileName =
    previewDocument?.type === "matter-file"
      ? previewDocument.document.name
      : previewDocument?.type === "firm-file"
        ? previewDocument.document.name
        : previewDocument?.type === "matter-template"
          ? previewDocument.document.name
          : "";

  const fileType =
    previewDocument?.type === "firm-file" ? "firm-file" : "matter-file";

  const presignedUrlKey =
    previewDocument?.type === "matter-file"
      ? previewDocument.document.presignedUrlKey
      : previewDocument?.type === "firm-file"
        ? previewDocument.document.presignedUrlKey
        : previewDocument?.type === "matter-template"
          ? previewDocument.document.presignedUrlKey
          : "";
  const fileId =
    previewDocument?.type === "matter-file" ||
    previewDocument?.type === "firm-file"
      ? previewDocument.document.id
      : undefined;

  const signerSlots = useMemo<SignerSlot[]>(() => {
    const slots: SignerSlot[] = [];
    signers.forEach((signer, index) => {
      const slotIndex = getSignatureRequestSignerSlotIndex(signers, index);
      if (isContactSigner(signer)) {
        slots.push({
          id: signer.contact.id,
          displayName: formatContactsName(signer.contact) || undefined,
          kind: "contact",
          label: `Signer${slotIndex + 1}`,
          colorIndex: signer.colorIndex,
          signerOrder: index + 1,
          slotName: `Signer ${slotIndex + 1}`,
        });
        return;
      }

      if (isUserSigner(signer)) {
        slots.push({
          id: signer.user.id,
          displayName: formatUsersName(signer.user) || undefined,
          kind: "user",
          label: `UserSigner${slotIndex + 1}`,
          colorIndex: signer.colorIndex,
          signerOrder: index + 1,
          slotName: `Firm signer ${slotIndex + 1}`,
        });
      }
    });

    return slots;
  }, [signers]);

  if (
    !fileName ||
    (!presignedUrlKey && !fileId) ||
    !fileName.toLowerCase().endsWith(".pdf")
  ) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-center">
        <div className="space-y-2">
          <Typography className="text-foreground text-sm font-medium">
            Field placement is only available for PDF documents.
          </Typography>
          <Typography className="text-muted-foreground text-xs">
            You can still create and send the signature request — signers will
            use the full document.
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 w-full overflow-hidden">
      <SignatureRequestPdfLayoutEditor
        fileName={fileName}
        fileType={fileType}
        fileId={fileId}
        presignedUrlKey={presignedUrlKey ?? ""}
        signers={signerSlots}
        activeSignerId={activeSignerId}
        onActiveSignerChange={onActiveSignerChange}
        onRequestSignerSlot={onRequestSignerSlot}
        onPendingSignerSlotsChange={onPendingSignerSlotsChange}
        fields={layoutFields}
        onFieldsChange={onLayoutFieldsChange}
      />
    </div>
  );
}
